import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Mic, Square } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { saveBlobsToIndexedDB, deleteBlobsByQuestionId } from "@/lib/blobStorage";
import { uploadQueue } from "@/lib/uploadQueue";
import { generateSecureFileUrl } from "@/utils/secure-storage";

const getSupportedAudioMimeType = (): string | undefined => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/aac',
  ];
  return types.find(type => MediaRecorder.isTypeSupported(type));
};

interface AudioRecorderProps {
  onRecordingComplete: (url: string) => void;
  onUploadStateChange?: (isUploading: boolean) => void;
  testResponseId?: string;
  questionId?: string;
}

export function AudioRecorder({ 
  onRecordingComplete, 
  onUploadStateChange,
  testResponseId,
  questionId 
}: AudioRecorderProps) {
  const { toast } = useToast();
  const [isRecording, setIsRecording] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [actualAudioMimeType, setActualAudioMimeType] = useState<string>('audio/webm');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const currentUploadIdRef = useRef<string | null>(null);

  // Subscribe to upload queue for this question's upload status
  useEffect(() => {
    if (!questionId) return;

    const unsubscribe = uploadQueue.subscribe(async (state) => {
      const upload = state.uploads.find(u => u.questionId === questionId);
      if (upload && currentUploadIdRef.current === upload.id) {
        if (upload.status === 'complete' && upload.audioPath) {
          // Generate secure URL for the uploaded audio
          try {
            const secureUrl = await generateSecureFileUrl('test-responses', upload.audioPath);
            if (secureUrl) {
              onRecordingComplete(secureUrl);
            }
          } catch (error) {
            console.error('[AudioRecorder] Error generating secure URL:', error);
          }
          setIsUploading(false);
          onUploadStateChange?.(false);
        } else if (upload.status === 'failed') {
          setIsUploading(false);
          onUploadStateChange?.(false);
          toast({
            title: "Upload failed",
            description: "Could not upload audio. Please try again.",
            variant: "destructive",
          });
        }
      }
    });

    return unsubscribe;
  }, [questionId, onRecordingComplete, onUploadStateChange, toast]);

  const startRecording = async () => {
    try {
      setIsPreparing(true);
      chunksRef.current = [];
      
      const audioMimeType = getSupportedAudioMimeType();
      if (!audioMimeType) {
        throw new Error('No supported audio format found for this browser');
      }
      setActualAudioMimeType(audioMimeType);
      console.log(`[AudioRecorder] Using audio MIME type: ${audioMimeType}`);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      streamRef.current = stream;

      const audioRecorderOptions = { 
        mimeType: audioMimeType, 
        audioBitsPerSecond: 128000 
      };
      const mediaRecorder = new window.MediaRecorder(stream, audioRecorderOptions);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setIsPreparing(false);
    } catch (error) {
      console.error("Error starting audio recording:", error);
      setIsPreparing(false);
      toast({
        title: "Recording failed",
        description: "Could not access microphone. Please check permissions.",
        variant: "destructive",
      });
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current || !isRecording) return;

    setIsRecording(false);
    setIsUploading(true);
    onUploadStateChange?.(true);

    // Stop the recorder and wait for all data
    mediaRecorderRef.current.stop();

    // Wait for the onstop event to fire and process the data
    await new Promise<void>((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve();
        return;
      }

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: actualAudioMimeType });
        chunksRef.current = [];

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }

        // If we have testResponseId and questionId, use TUS upload
        if (testResponseId && questionId) {
          try {
            // Clear any previous uploads for this question
            await deleteBlobsByQuestionId(questionId);

            // Save to IndexedDB and queue for TUS upload
            console.log('[AudioRecorder] Saving to IndexedDB and queueing TUS upload');
            const uploadId = await uploadQueue.enqueue(
              questionId,
              testResponseId,
              undefined, // no video
              audioBlob,
              undefined, // no video mime type
              actualAudioMimeType
            );

            currentUploadIdRef.current = uploadId;
            console.log('[AudioRecorder] Upload queued with ID:', uploadId);
          } catch (error) {
            console.error('[AudioRecorder] Error queueing upload:', error);
            setIsUploading(false);
            onUploadStateChange?.(false);
            toast({
              title: "Upload failed",
              description: "Could not queue audio upload. Please try again.",
              variant: "destructive",
            });
          }
        } else {
          // Fallback to direct upload for backward compatibility
          console.log('[AudioRecorder] Using direct upload (no testResponseId/questionId)');
          try {
            const { supabase } = await import('@/integrations/supabase/client');
            const fileName = `${crypto.randomUUID()}.webm`;
            
            const { error } = await supabase.storage
              .from('test-responses')
              .upload(fileName, audioBlob, {
                contentType: actualAudioMimeType,
                upsert: false
              });

            if (error) throw error;

            const secureUrl = await generateSecureFileUrl('test-responses', fileName);
            if (secureUrl) {
              onRecordingComplete(secureUrl);
            }
          } catch (error) {
            console.error('Error uploading audio:', error);
            toast({
              title: "Upload failed",
              description: "Could not upload audio. Please try again.",
              variant: "destructive",
            });
          } finally {
            setIsUploading(false);
            onUploadStateChange?.(false);
          }
        }

        resolve();
      };
    });
  };

  return (
    <div className="flex justify-center">
      {!isRecording ? (
        <Button
          onClick={startRecording}
          disabled={isPreparing || isUploading}
          className="w-32"
        >
          {isPreparing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Mic className="h-4 w-4 mr-2" />
          )}
          Record
        </Button>
      ) : (
        <Button
          onClick={stopRecording}
          variant="destructive"
          disabled={isUploading}
          className="w-32"
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Square className="h-4 w-4 mr-2" />
          )}
          {isUploading ? "Saving..." : "Stop"}
        </Button>
      )}
    </div>
  );
}
